import { GridColumns, useGridApiRef } from '@mui/x-data-grid-pro'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete } from 'react-icons/md'
import { toast } from 'react-toastify'
import { useRemoveLocalityService } from 'src/api/locality-service'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { OverFlowMenu } from 'src/components/OverFlowMenu'
import { APIResultCount } from 'src/constant'
import { useDialogCtx } from 'src/hooks/context-hooks'
import {
  invalidateServiceCache,
  useDeleteService,
  useGetService,
  useGetServices,
  useUpdateService,
} from '../../../../api/frequency'
import { invalidateLocalityCache } from '../../../../api/localities'
import { invalidateLocalityFrequencyCache } from '../../../../api/locality-frequency'
import { invalidateMooringCache, useGetMooringsForService } from '../../../../api/moorings'
import { CenteredSpinner } from '../../../../components/Spinner'
import GenericTable from '../../../../components/table/GenericTable'
import { IMooring, IPositionType } from '../../../../generated-types'
import { IFrequency } from '../../../../generated-types/frequency'
import { IFrequencyPeriodType } from '../../../../generated-types/frequency-period-type'
import { ShowDialogProps } from '../../../../show-dialog'
import { FrequencyForm } from './FrequencyForm'

interface UpdateFrequencyProps extends ShowDialogProps {
  customerId: EntityId
  serviceId: EntityId
  frequency: IFrequency
  selectedMoorings: IMooring[]
}
const UpdateFrequencyDialog: React.FC<UpdateFrequencyProps> = ({
  pop,
  customerId,
  serviceId,
  frequency,
  selectedMoorings,
}) => {
  const { t } = useTranslation(['frequency', 'position', 'common', 'service'])

  const { mutateAsync } = useUpdateService(customerId as EntityId, frequency.id)
  const onSubmit = async form => {
    const data = await mutateAsync(form, {
      onSuccess: () => {
        const localityIdList = [] as number[]
        selectedMoorings.forEach(mooring => {
          if (localityIdList.indexOf(mooring.localityId) == -1) {
            localityIdList.push(mooring.localityId)
            invalidateLocalityFrequencyCache.getLocalityFrequenciesAndDetails(customerId, mooring.localityId)
          }
        })
        invalidateServiceCache.getServices(customerId as EntityId)
        invalidateServiceCache.getService(customerId as EntityId, frequency.id)
        invalidateLocalityFrequencyCache.getAllLocalityFrequencies(customerId)
        invalidateServiceCache.getServices(customerId as EntityId)
        toast.success(t('serviceUpdatedSuccessfully', { ns: 'service' }))
        pop()
      },
      onError: _ => {
        toast.error(t('updatingError', { ns: 'common' }))
      },
    })
    return data
  }

  const { data, isLoading } = useGetService(customerId as EntityId, frequency.id)

  if (isLoading) return <CenteredSpinner />

  return (
    <FrequencyForm
      onSubmit={onSubmit}
      onCancel={() => pop()}
      initialValues={data}
      title={t('updateService', { ns: 'service' })}
      disableFrequencyEditing={frequency.moorings && frequency.moorings.length > 0}
    />
  )
}

interface FrequencyTableProps {
  customerId: EntityId
}
export const FrequencyTable: React.FC<FrequencyTableProps> = ({ customerId }) => {
  const { t: translate } = useTranslation(['frequency', 'common', 'mooring-service'])
  const apiRef = useGridApiRef()
  const { showDialog, showConfirmDialog } = useDialogCtx()

  const { data: frequencies } = useGetServices(customerId as EntityId, 1)
  const [selectedService, setSelectedService] = useState('0')

  const { mutateAsync: deleteMooring } = useRemoveLocalityService(customerId, selectedService)
  const onRemoveMooring = useCallback(
    (mooringId: EntityId, row: IMooring) => {
      showConfirmDialog(
        translate('removeMooringConfirmationHeader', { ns: 'mooring-service' }),
        translate('areYouSureRemoveMooring', { ns: 'mooring-service' }),
        {
          acceptText: translate('remove', { ns: 'common' }),
          cancelText: translate('cancel', { ns: 'common' }),
        },
        async pop => {
          await deleteMooring(mooringId, {
            onSuccess: () => {
              toast.success(translate('mooringRemovedSuccessfully', { ns: 'mooring-service' }))
              invalidateServiceCache.getServices(customerId)
              invalidateMooringCache.getMooringsForService(customerId, selectedService)
              invalidateLocalityFrequencyCache.getAllLocalityFrequencies(customerId, row?.localityId)
              invalidateLocalityCache.getLocalitiesWithInfoCount(customerId as EntityId)
              pop()
            },
            onError: () => {
              toast.error(translate('deletingError', { ns: 'common' }))
            },
          })
        },
      )
    },
    [selectedService],
  )

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'code',
      headerName: '',
      maxWidth: 50,
    },
    {
      field: 'title',
      headerName: translate('title', { ns: 'common' }),
      flex: 1,
    },
    {
      field: 'periodFrequency',
      headerName: translate('frequency', { ns: 'frequency' }),
      flex: 1,
      valueGetter: ({ row }) => {
        return (
          row.periodFrequency + ' ' + translate(IFrequencyPeriodType[row.periodType].toLowerCase(), { ns: 'common' })
        )
      },
    },
    {
      field: 'moorings',
      headerName: translate('moorings', { ns: 'frequency' }),
      flex: 1.5,
      renderCell: ({ row }) => {
        return (
          <OverFlowMenu
            visibleCount={1}
            items={row.moorings.map(m => {
              const [key, ...rest] = m.name.split('|')
              const translated = translate(key.trim(), { ns: 'mooring' })

              return {
                id: m.id,
                name: `${translated} |${rest.join('|')}`,
                localityId: m.localityId,
              } as IMooring
            })}
            actionIcon={<MdDelete size={20} />}
            onAction={(id, item) => {
              setSelectedService(row.id)
              onRemoveMooring(id, item)
            }}
          />
        )
      },
    },
    {
      field: 'positionTypes',
      headerName: translate('toBeControlled', { ns: 'frequency' }),
      flex: 1,
      valueGetter: ({ value }) => {
        return (value?.map(x => translate(IPositionType[x].toString(), { ns: 'position' })).join(',') as string) || '-'
      },
    },
  ]

  const { mutateAsync, isLoading: isDeleting } = useDeleteService(customerId as EntityId)
  const { data: mooringsForService } = useGetMooringsForService(customerId as EntityId, 1, 0, APIResultCount.Max)

  const onDelete = (id: EntityId) => {
    showConfirmDialog(
      translate('deleteService', { ns: 'service' }),
      translate('areYouSureDeleteService', { ns: 'service' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        await mutateAsync(id, {
          onSuccess: () => {
            invalidateLocalityCache.getLocalities(customerId as EntityId)
            invalidateServiceCache.getServices(customerId as EntityId)
            invalidateLocalityFrequencyCache.getAllLocalityFrequencies(customerId)

            const localityIdList = [] as number[]
            mooringsForService?.items.forEach(mooring => {
              if (localityIdList.indexOf(mooring.localityId) == -1) {
                localityIdList.push(mooring.localityId)
                invalidateLocalityFrequencyCache.getLocalityFrequenciesAndDetails(customerId, mooring.localityId)
              }
            })
            invalidateServiceCache.getService(customerId as EntityId, id)
            invalidateLocalityCache.getLocalitiesWithInfoCount(customerId as EntityId)
            toast.success(translate('serviceDeletedSuccessfully', { ns: 'service' }))
            pop()
          },
          onError: () => {
            toast.error(translate('deletingError', { ns: 'common' }))
          },
        })
      },
    )
  }

  const onUpdate = async (frequency: IFrequency) => {
    await showDialog(
      UpdateFrequencyDialog,
      {
        componentProps: {
          customerId: customerId as EntityId,
          frequency,
          selectedMoorings: mooringsForService?.items as IMooring[],
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        headerName: '',
        width: 60,
        renderCell: params => {
          const tooltipText =
            params.row.moorings && params.row.moorings.length > 0
              ? translate('fieldsDisabledDueToMoorings', { ns: 'service' })
              : ''
          return (
            <>
              <GridActionButton
                key={params.id}
                buttonIcon={<MdDelete size={24} />}
                onClick={e => {
                  e.stopPropagation()
                  onDelete(params.id)
                }}
                disabled={isDeleting || (params.row.moorings && params.row.moorings.length > 0)}
                toolTipTitle={tooltipText}
                width="40px"
                color="primary"
              />
            </>
          )
        },
      },
    ],
    [isDeleting, mooringsForService, UpdateFrequencyDialog],
  )

  const isScrollable = (frequencies?.items?.length ?? 0) > 5

  return (
    <GenericTable
      key={isScrollable ? 'scrollable' : 'auto-height'}
      id="frequencies-table"
      columns={columns}
      data={frequencies?.items || []}
      isDownloading={isDeleting}
      apiRef={apiRef}
      enableFilter={false}
      getRowHeight={() => 44}
      autoHeight={!isScrollable}
      height={isScrollable ? '30vh' : undefined}
      onRowClick={({ row }) => onUpdate(row as IFrequency)}
    />
  )
}
